import { z } from 'zod';
import {
  ACTUAL_MODALITY_VALUES,
  ATTENDANCE_STATUS_VALUES,
  DEVICE_REGISTRATION_MODE_VALUES,
  LATE_POLICY_VALUES,
  LOAD_TYPE_VALUES,
  LOCATION_TYPE_VALUES,
  ONLINE_PLATFORM_VALUES,
  PAYABLE_HOUR_POLICY_VALUES,
  ROUNDING_POLICY_VALUES,
  SCHEDULED_MODALITY_VALUES,
  SESSION_TYPE_VALUES,
  USER_VERIFICATION_MODE_VALUES,
  VERIFICATION_STATUS_VALUES,
  WORK_ARRANGEMENT_VALUES,
  type AttendanceRules,
} from '@fas/types';
import {
  booleanQuerySchema,
  effectiveDatesShape,
  isoDateSchema,
  latitudeSchema,
  longitudeSchema,
  nonEmptyTrimmed,
  optionalText,
  paginationQuerySchema,
  refineEffectiveRange,
  uuidSchema,
} from './common';
import { webauthnAssertionSchema } from './biometrics';
import { deviceProofSchema, offlineDeviceProofSchema } from './devices';
import { scanReceiptSchema } from './displays';

// ── Attendance rules (§13, §28, §70) ───────────────────────────────────────────
const minutes = (max: number) => z.number().int().min(0).max(max);

export const attendanceRulesSchema = z
  .object({
    clockInEarlyWindowMinutes: minutes(720),
    clockInLateWindowMinutes: minutes(720),
    allowClockInOutsideWindow: z.boolean(),
    lateGraceMinutes: minutes(240),
    latePolicy: z.enum(LATE_POLICY_VALUES),
    veryLateThresholdMinutes: minutes(720),
    clockOutEarlyToleranceMinutes: minutes(240),
    clockOutLateWindowMinutes: minutes(1440),
    minimumRenderedMinutes: minutes(1440),
    maximumRenderedMinutes: z.number().int().min(1).max(1440).nullable(),
    gpsRequiredForOnsite: z.boolean(),
    minimumGpsAccuracyMeters: z.number().int().min(5).max(5000),
    geofenceRadiusMeters: z.number().int().min(10).max(20000),
    allowOutsideGeofenceWithReview: z.boolean(),
    allowClockInWithoutGps: z.boolean(),
    impossibleTravelSpeedKmh: z.number().int().min(10).max(2000),
    deviceRegistration: z.enum(DEVICE_REGISTRATION_MODE_VALUES),
    flagSharedDevices: z.boolean(),
    userVerification: z.enum(USER_VERIFICATION_MODE_VALUES),
    allowOfflineCapture: z.boolean(),
    requireQrScanOnsite: z.boolean(),
    allowedHybridModes: z
      .array(z.enum(['ONSITE', 'ONLINE', 'HYBRID_COMBINATION'] as const))
      .min(1, 'Allow at least one mode')
      .refine((v) => new Set(v).size === v.length, 'Modes must not repeat'),
    proofRequiredOnline: z.boolean(),
    proofRequiredPartTime: z.boolean(),
    proofRequiredForException: z.boolean(),
    meetingLogCountsAsProof: z.boolean(),
    meetingAttendanceMinPercent: z.number().int().min(0).max(100),
    dailyClockInEarlyWindowMinutes: minutes(720),
    allowWorkFromHome: z.boolean(),
    allowOfficialBusiness: z.boolean(),
    roundingPolicy: z.enum(ROUNDING_POLICY_VALUES),
    payableHourPolicy: z.enum(PAYABLE_HOUR_POLICY_VALUES),
    excessMinutesRequireApproval: z.boolean(),
    requireChairVerification: z.boolean(),
    requireDeanApproval: z.boolean(),
    requireHrFinalization: z.boolean(),
    lockAfterPayrollReady: z.boolean(),
    maxCorrectionsPerCutoff: z.number().int().min(0).max(100),
    allowMakeupCreditWhenOriginalCredited: z.boolean(),
  })
  .strict() satisfies z.ZodType<AttendanceRules>;

/** Overrides of a scoped policy: any subset of the rules, unknown keys rejected. */
export const attendanceRulesOverrideSchema = attendanceRulesSchema.partial().strict();

function refineRules(v: { rules?: Partial<AttendanceRules> }, ctx: z.RefinementCtx) {
  const r = v.rules;
  if (!r) return;
  if (r.veryLateThresholdMinutes !== undefined && r.lateGraceMinutes !== undefined && r.veryLateThresholdMinutes < r.lateGraceMinutes) {
    ctx.addIssue({ code: 'custom', path: ['rules', 'veryLateThresholdMinutes'], message: 'Must be at least the grace period' });
  }
  if (r.maximumRenderedMinutes && r.minimumRenderedMinutes !== undefined && r.maximumRenderedMinutes < r.minimumRenderedMinutes) {
    ctx.addIssue({ code: 'custom', path: ['rules', 'maximumRenderedMinutes'], message: 'Must be at least the minimum duration' });
  }
}

const policyScope = {
  collegeId: uuidSchema.nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  employmentTypeCode: z.string().trim().toUpperCase().max(30).nullable().optional(),
  loadType: z.enum(LOAD_TYPE_VALUES).nullable().optional(),
  scheduledModality: z.enum(SCHEDULED_MODALITY_VALUES).nullable().optional(),
};

function refineScope(v: { collegeId?: string | null; departmentId?: string | null }, ctx: z.RefinementCtx) {
  if (v.collegeId && v.departmentId) {
    ctx.addIssue({ code: 'custom', path: ['departmentId'], message: 'Choose either a college or a department, not both' });
  }
}

export const createAttendancePolicySchema = z
  .object({
    name: nonEmptyTrimmed(150, 'Name'),
    description: optionalText(1000),
    ...policyScope,
    rules: attendanceRulesOverrideSchema,
  })
  .superRefine((v, ctx) => {
    refineScope(v, ctx);
    refineRules(v, ctx);
  });
export type CreateAttendancePolicyInput = z.infer<typeof createAttendancePolicySchema>;

/** Scope is fixed after creation; create a new policy to target another scope. */
export const updateAttendancePolicySchema = z
  .object({
    name: nonEmptyTrimmed(150, 'Name').optional(),
    description: optionalText(1000),
    rules: attendanceRulesOverrideSchema.optional(),
  })
  .superRefine(refineRules);
export type UpdateAttendancePolicyInput = z.infer<typeof updateAttendancePolicySchema>;

export const listAttendancePoliciesQuerySchema = paginationQuerySchema.extend({ active: booleanQuerySchema.optional() });
export type ListAttendancePoliciesQuery = z.infer<typeof listAttendancePoliciesQuerySchema>;

export const resolveAttendanceRulesQuerySchema = z.object({
  facultyId: uuidSchema.optional(),
  scheduleId: uuidSchema.optional(),
  ...policyScope,
});
export type ResolveAttendanceRulesQuery = z.infer<typeof resolveAttendanceRulesQuerySchema>;

// ── Geofences (§14.2) ──────────────────────────────────────────────────────────
const geofenceBase = z.object({
  name: nonEmptyTrimmed(150, 'Name'),
  locationType: z.enum(LOCATION_TYPE_VALUES),
  campusId: uuidSchema.nullable().optional(),
  buildingId: uuidSchema.nullable().optional(),
  locationId: uuidSchema.nullable().optional(),
  latitude: latitudeSchema,
  longitude: longitudeSchema,
  radiusMeters: z.number().int().min(10, 'At least 10 m').max(20000, 'At most 20 km'),
  minimumAccuracyMeters: z.number().int().min(5).max(5000),
  ...effectiveDatesShape,
  remarks: optionalText(1000),
});

export const createGeofenceSchema = geofenceBase.superRefine((v, ctx) => {
  const anchors = [v.campusId, v.buildingId, v.locationId].filter(Boolean).length;
  if (anchors !== 1) {
    ctx.addIssue({ code: 'custom', path: ['locationId'], message: 'Attach the geofence to exactly one campus, building or location' });
  }
  refineEffectiveRange(v, ctx);
});
export type CreateGeofenceInput = z.infer<typeof createGeofenceSchema>;

/** The anchor cannot change; create a new geofence for another place. */
export const updateGeofenceSchema = geofenceBase.omit({ campusId: true, buildingId: true, locationId: true }).partial().superRefine(refineEffectiveRange);
export type UpdateGeofenceInput = z.infer<typeof updateGeofenceSchema>;

export const listGeofencesQuerySchema = paginationQuerySchema.extend({
  active: booleanQuerySchema.optional(),
  campusId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
});
export type ListGeofencesQuery = z.infer<typeof listGeofencesQuerySchema>;

// ── Clock-in / clock-out (§12, §46.4) ──────────────────────────────────────────
export const GPS_FAILURE_VALUES = ['PERMISSION_DENIED', 'UNAVAILABLE', 'TIMEOUT'] as const;

export const gpsReadingSchema = z.object({
  latitude: latitudeSchema,
  longitude: longitudeSchema,
  accuracyMeters: z.number().min(0).max(100_000).nullable().optional(),
});

/** A scan of a room or kiosk display, exchanged for this receipt by `POST /scan` (Phase 2E). */
export const scanEvidenceSchema = z.object({ receipt: scanReceiptSchema }).strict();

const clockCommon = {
  /**
   * Browser clients send WEB_BROWSER; the installed mobile PWA sends MOBILE_GPS; QR_CODE when
   * the event comes from scanning a display, NFC_TAG from tapping a room tag (both with `scan`).
   */
  method: z.enum(['WEB_BROWSER', 'MOBILE_GPS', 'QR_CODE', 'NFC_TAG'] as const).default('WEB_BROWSER'),
  location: gpsReadingSchema.nullable().optional(),
  gpsError: z.enum(GPS_FAILURE_VALUES).nullable().optional(),
  /** Idempotency key (§12.4): the same key always returns the same result. */
  clientRequestId: uuidSchema,
  /** Diagnostic only; never authoritative (§71). */
  clientTimestamp: z.iso.datetime({ offset: true }).optional(),
  remarks: optionalText(500),
  /** Signed by the device key over the purpose, a server challenge and `clientRequestId` (Phase 2B). */
  device: deviceProofSchema.optional(),
  /** Presence evidence from a scanned room or kiosk code (Phase 2E). */
  scan: scanEvidenceSchema.optional(),
  /** The device verified its user: a WebAuthn assertion over a clock challenge (Phase 3H). */
  userVerification: webauthnAssertionSchema.optional(),
};

function refineGps(v: { location?: unknown; gpsError?: unknown }, ctx: z.RefinementCtx) {
  if (v.location && v.gpsError) ctx.addIssue({ code: 'custom', path: ['gpsError'], message: 'Send either a location or a GPS error, not both' });
}

function refineScan(v: { method?: string; scan?: unknown }, ctx: z.RefinementCtx) {
  if ((v.method === 'QR_CODE' || v.method === 'NFC_TAG') !== !!v.scan) {
    ctx.addIssue({ code: 'custom', path: ['scan'], message: 'A scan goes with the method QR_CODE (a display’s code) or NFC_TAG (a room tag), and those methods need a scan' });
  }
}

export const clockInSchema = z
  .object({
    /** An existing session (from GET /attendance/today). */
    sessionId: uuidSchema.optional(),
    /** Or the schedule of a class meeting today. */
    scheduleId: uuidSchema.optional(),
    /** DAILY = full-time daily time-in (§21). */
    sessionType: z.enum(['CLASS', 'DAILY'] as const).default('CLASS'),
    actualModality: z.enum(ACTUAL_MODALITY_VALUES).optional(),
    workArrangement: z.enum(WORK_ARRANGEMENT_VALUES).optional(),
    onlinePlatform: z.enum(ONLINE_PLATFORM_VALUES).nullable().optional(),
    onlineMeetingRef: optionalText(200),
    ...clockCommon,
  })
  .superRefine((v, ctx) => {
    refineGps(v, ctx);
    refineScan(v, ctx);
    if (v.sessionType === 'CLASS' && !v.sessionId && !v.scheduleId) {
      ctx.addIssue({ code: 'custom', path: ['scheduleId'], message: 'Choose the class to clock in to' });
    }
    if (v.sessionType === 'CLASS' && v.workArrangement) {
      ctx.addIssue({ code: 'custom', path: ['workArrangement'], message: 'Work arrangement applies to daily attendance only' });
    }
  });
export type ClockInInput = z.infer<typeof clockInSchema>;

export const clockOutSchema = z
  .object({
    /** Required when more than one session is open (e.g. a class during a full-time workday). */
    sessionId: uuidSchema.optional(),
    ...clockCommon,
  })
  .superRefine((v, ctx) => {
    refineGps(v, ctx);
    refineScan(v, ctx);
  });
export type ClockOutInput = z.infer<typeof clockOutSchema>;

// ── Clock events recorded offline (§57, Phase 2D) ──────────────────────────────
const SEVEN_DAYS_MS = 7 * 86_400_000;

const offlineCommon = {
  clientRequestId: uuidSchema,
  /** The session from the day's list cached on the device. */
  sessionId: uuidSchema,
  /** Device time when the event was recorded — untrusted; corrected by `skewMs`. */
  capturedAt: z.iso.datetime({ offset: true }),
  /** Server time minus device time (ms), measured at the device's last contact with the server. */
  skewMs: z.number().int().min(-SEVEN_DAYS_MS).max(SEVEN_DAYS_MS),
  method: z.enum(['WEB_BROWSER', 'MOBILE_GPS'] as const).default('WEB_BROWSER'),
  location: gpsReadingSchema.nullable().optional(),
  gpsError: z.enum(GPS_FAILURE_VALUES).nullable().optional(),
  remarks: optionalText(500),
  /** Signed on the device at capture, without a server challenge. */
  device: offlineDeviceProofSchema.optional(),
};

export const offlineClockEventSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('CLOCK_IN'),
      sessionType: z.enum(['CLASS', 'DAILY'] as const).default('CLASS'),
      actualModality: z.enum(ACTUAL_MODALITY_VALUES).optional(),
      workArrangement: z.enum(WORK_ARRANGEMENT_VALUES).optional(),
      onlinePlatform: z.enum(ONLINE_PLATFORM_VALUES).nullable().optional(),
      onlineMeetingRef: optionalText(200),
      ...offlineCommon,
    })
    .superRefine(refineGps),
  z.object({ kind: z.literal('CLOCK_OUT'), ...offlineCommon }).superRefine(refineGps),
]);
export type OfflineClockEventInput = z.infer<typeof offlineClockEventSchema>;

export const offlineSyncSchema = z
  .object({ events: z.array(offlineClockEventSchema).min(1).max(20) })
  .strict()
  .refine((v) => new Set(v.events.map((e) => e.clientRequestId)).size === v.events.length, { path: ['events'], message: 'Each event may appear once' });
export type OfflineSyncInput = z.infer<typeof offlineSyncSchema>;

// ── Queries ────────────────────────────────────────────────────────────────────
function refineRange(v: { from?: string; to?: string }, ctx: z.RefinementCtx, maxDays: number) {
  if (v.from && v.to) {
    if (v.to < v.from) ctx.addIssue({ code: 'custom', path: ['to'], message: 'Must be on or after "from"' });
    else if ((Date.parse(v.to) - Date.parse(v.from)) / 86_400_000 > maxDays) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: `Range cannot exceed ${maxDays} days` });
    }
  }
}

export const listAttendanceQuerySchema = paginationQuerySchema
  .extend({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    facultyId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    collegeId: uuidSchema.optional(),
    scheduleId: uuidSchema.optional(),
    sessionType: z.enum(SESSION_TYPE_VALUES).optional(),
    attendanceStatus: z.enum(ATTENDANCE_STATUS_VALUES).optional(),
    verificationStatus: z.enum(VERIFICATION_STATUS_VALUES).optional(),
    flagged: booleanQuerySchema.optional(),
  })
  .superRefine((v, ctx) => refineRange(v, ctx, 370));
export type ListAttendanceQuery = z.infer<typeof listAttendanceQuerySchema>;

export const attendanceTodayQuerySchema = z.object({ date: isoDateSchema.optional() });
export type AttendanceTodayQuery = z.infer<typeof attendanceTodayQuerySchema>;

export const attendanceSummaryQuerySchema = z
  .object({ facultyId: uuidSchema.optional(), from: isoDateSchema.optional(), to: isoDateSchema.optional() })
  .superRefine((v, ctx) => refineRange(v, ctx, 370));
export type AttendanceSummaryQuery = z.infer<typeof attendanceSummaryQuerySchema>;

export const dtrQuerySchema = z
  .object({ facultyId: uuidSchema.optional(), from: isoDateSchema, to: isoDateSchema })
  .superRefine((v, ctx) => refineRange(v, ctx, 62));
export type DtrQuery = z.infer<typeof dtrQuerySchema>;

export const certifyAttendanceSchema = z
  .object({ from: isoDateSchema, to: isoDateSchema })
  .superRefine((v, ctx) => refineRange(v, ctx, 62));
export type CertifyAttendanceInput = z.infer<typeof certifyAttendanceSchema>;

export const dashboardQuerySchema = z.object({
  date: isoDateSchema.optional(),
  collegeId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
});
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

// ── Notifications (§36) ────────────────────────────────────────────────────────
export const listNotificationsQuerySchema = paginationQuerySchema.extend({ unread: booleanQuerySchema.optional() });
export type ListNotificationsQuery = z.infer<typeof listNotificationsQuerySchema>;
